import moment from "moment";

import {
    NullableNumber,
    ReportEdgeItem,
    ReportIndividualData,
    ReportIndividualDataSingle,
    ReportIndividualDataSingleType,
    ReportIndividualEdgeItem,
    ReportTimeRangeType,
} from "types/Reports";
import { calculatePercentage } from "utils/functions";
import { computeDayInMonthByIndex } from "./reportCompute";

export const sumArrayAndIgnoreNaN = (input: NullableNumber[]) => {
    if (input.length === 0) {
        return 0;
    }

    let sum = null;

    for (const item of input) {
        if (item !== null) {
            if (sum === null) {
                sum = item;
            } else {
                sum += item;
            }
        }
    }

    return sum;
};

export const sumByWithNull = <T>(array: T[], retrievePropertyFunc: (item: T, index: number) => NullableNumber): NullableNumber => {
    let sum: NullableNumber = null;

    for (let i = 0; i < array.length; i++) {
        const item = array[i];

        const value = retrievePropertyFunc(item, i);

        if (sum === null) {
            sum = value;
        } else if (value !== null) {
            sum += value;
        }
    }

    return sum;
};

export const averageByWithNull = <T>(array: T[], retrievePropertyFunc: (item: T) => NullableNumber): NullableNumber => {
    let sum = 0;
    let count = 0;

    for (const item of array) {
        const value = retrievePropertyFunc(item);

        if (value !== null) {
            count++;
            sum += value;
        }
    }

    if (count === 0) {
        return null;
    }

    return sum / count;
};

export const getMinMaxByWithNull = <T>(array: T[], retrievePropertyFunc: (item: T, index: number) => NullableNumber) => {
    let min = null;
    let minIdx: number[] = [];
    let max = null;
    let maxIdx: number[] = [];

    for (let i = 0; i < array.length; i++) {
        const item = array[i];

        const value = retrievePropertyFunc(item, i);

        if (value !== null) {
            if (min === null || min > value) {
                min = value;
                minIdx = [i];
            } else if (min !== null && min === value) {
                minIdx.push(i);
            }

            if (max === null || max < value) {
                max = value;
                maxIdx = [i];
            } else if (max !== null && max === value) {
                maxIdx.push(i);
            }
        }
    }

    return { min, minIdx, max, maxIdx };
};

export const sumOfInputAtIdx = (idx: number, prevSum: NullableNumber, inputData: NullableNumber[][]) => {
    let sum: NullableNumber = prevSum;

    for (const arr of inputData) {
        if (arr.length > 0 && arr[idx] !== null) {
            if (sum === null) {
                sum = arr[idx];
            } else {
                // The reason why I have to do this because the linter doesn't work with number | null within array.
                // It keeps raising an error that the data[i] and arr[i] are possible to be null
                const next = arr[idx];

                if (sum !== null && next !== null) {
                    sum = sum + next;
                }
            }
        }
    }

    return sum;
};

export const getMinMaxFromConsumptionArray = (length: number, inputData: NullableNumber[][]) => {
    let lowestValue: NullableNumber = null;
    let lowestIndex: number[] = [];
    let highestValue: NullableNumber = null;
    let highestIndex: number[] = [];
    let count = 0;

    const data: NullableNumber[] = Array.from({length}, (_, _i) => null);

    for (let i = 0; i < length; i++) {
        data[i] = sumOfInputAtIdx(i, data[i], inputData);

        const cur = data[i];

        if (typeof cur === 'number' && cur !== null) {
            count++;

            if (lowestValue === null || lowestValue > cur) {
                lowestValue = cur;
                lowestIndex = [i];
            } else if (lowestValue !== null && lowestValue === cur) {
                lowestIndex.push(i);
            }

            if (highestValue === null || highestValue < cur) {
                highestValue = cur;
                highestIndex = [i];
            } else if (highestValue !== null && highestValue === cur) {
                highestIndex.push(i);
            }
        }
    }

    return { count, data, lowestValue, lowestIndex, highestValue, highestIndex };
};

export const getTotalMaxFromConsumptionArray = (inputData: NullableNumber[]) => {
    let highestIndex: NullableNumber = null;
    let highestValue: NullableNumber = null;
    let totalValue: NullableNumber = null;
    let count = 0;

    for (let i = 0; i < inputData.length; i++) {
        const cur = inputData[i];

        if (cur === null) {
            continue;
        }

        count++;

        if (highestValue === null || highestValue < cur) {
            highestValue = inputData[i];
            highestIndex = i;
        }

        if (totalValue === null) {
            totalValue = cur;
        } else {
            totalValue += cur;
        }
    }

    return { count, highestIndex, highestValue, totalValue };
};

export const isDataArrayValid = (data: NullableNumber[]) => {
    if (data.length === 0) {
        return false;
    }

    let isValid = false;

    for (const item of data) {
        if (item !== null) {
            isValid = true;
            break;
        }
    }

    return isValid;
};

export const getTotalAndPercentFromSeries = (
    data: NullableNumber[],
    prevData: NullableNumber[],
) => {
    const total = sumByWithNull(data, (v) => v);
    let previousTotal = null;
    let percent: NullableNumber = null;

    if (prevData.length > 0) {
        previousTotal = sumByWithNull(prevData, (v) => v);
        percent = calculatePercentage(total, previousTotal);
    }

    return { total, percent, previousTotal };
};

export const getAverageAndPercentFromSeries = (
    data: NullableNumber[],
    prevData: NullableNumber[],
) => {
    const avg = averageByWithNull(data, (v) => v);
    let previousAvg = null;
    let percent: NullableNumber = null;

    if (prevData.length > 0) {
        previousAvg = averageByWithNull(prevData, (v) => v);
        percent = calculatePercentage(avg, previousAvg);
    }

    return { avg, percent, previousAvg };
};

export const getLowestHighestFromArray = (
    data: NullableNumber[],
    categories: string[],
    lastDayOfMonth: moment.Moment,
) => {
    const minMaxResult = getMinMaxByWithNull(data, (v) => v);

    const numberOfDayInMonth = lastDayOfMonth.daysInMonth();
    const highestItem: ReportEdgeItem = {
        value: minMaxResult.max,
        dates: minMaxResult.maxIdx.map((idx) => {
            const maxIndex = Number.parseInt(categories[idx]);
            return computeDayInMonthByIndex(maxIndex, numberOfDayInMonth, lastDayOfMonth);
        }),
    };
    const lowestItem: ReportEdgeItem = {
        value: minMaxResult.min,
        dates: minMaxResult.minIdx.map((idx) => {
            const minIndex = Number.parseInt(categories[idx]);
            return computeDayInMonthByIndex(minIndex, numberOfDayInMonth, lastDayOfMonth);
        }),
    };

    return { highestItem, lowestItem };
};

export const findLowestHighestOfIndividualData = (input: ReportIndividualDataSingle[]) => {
    let highest: ReportIndividualEdgeItem = {
        names: [],
        value: null,
    };
    let lowest: ReportIndividualEdgeItem = {
        names: [],
        value: null,
    };

    for (const item of input) {
        // empty or has another higher item
        if (
            (highest.value === null && item.value !== null) ||
            (highest.value !== null && item.value !== null && highest.value < item.value)
        ) {
            highest = {
                names: [item.name],
                value: item.value,
            };
        } else if (highest.value !== null && item.value !== null && highest.value === item.value) {
            highest.names.push(item.name);
        }

        if (
            (lowest.value === null && item.value !== null) ||
            (lowest.value !== null && item.value !== null && lowest.value > item.value)
        ) {
            lowest = {
                names: [item.name],
                value: item.value,
            };
        } else if (lowest.value !== null && item.value !== null && lowest.value === item.value) {
            lowest.names.push(item.name);
        }
    }

    return { highest, lowest };
};

/**
 * Process array of ReportIndividualData to prepare data for drawing individual table
 * @param type all or weekdays or weekends
 * @param data is the report individual data of a type
 * @returns
 */
export const getLowestHighestAndSortIndividualData = (type: ReportTimeRangeType, data: ReportIndividualData[]) => {
    const sortedData: ReportIndividualDataSingle[] = data.map((item) => {
        const single: ReportIndividualDataSingleType = {...item[type]};

        return {
            name: item.name,
            id: item.id,
            ...single,
        }
    });

    sortedData.sort((a, b) => {
        if (a.value === null && b.value === null) {
            return b.name.toLowerCase().localeCompare(a.name.toLowerCase());
        } else if (a.value === null) {
            return 1;
        } else if (b.value === null) {
            return -1;
        }

        return b.value - a.value;
    });

    const { highest, lowest } = findLowestHighestOfIndividualData(sortedData);

    return { highest, lowest, sortedData };
};
